// Live control presence is separate from SDK media readiness.
import { wsUrl } from "./api.js";

const HEARTBEAT_MS = 5000;
const DEAD_MS = 12000;

export class SessionLink {
  constructor(sessionId, onState, { onEnded, onReconnect } = {}) {
    this.sessionId = sessionId;
    this.onState = onState;
    this.onEnded = onEnded;
    this.onReconnect = onReconnect;
    this.closed = false;
    this.retry = 0;
    this.reconnectNotified = false;
    this.extraOnOpen = new Map();
    this.onVisible = () => {
      if (document.visibilityState === "visible") this.recover();
    };
    this.onOnline = () => this.recover(true);
    document.addEventListener("visibilitychange", this.onVisible);
    window.addEventListener("online", this.onOnline);
    this.connect();
  }

  recover(force = false) {
    if (this.closed || this.paused) return;
    if (force || !this.ws || this.ws.readyState > WebSocket.OPEN ||
        Date.now() - this.lastMsgAt > DEAD_MS) this.connect();
    else if (this.ws.readyState === WebSocket.OPEN) this.send("hb");
  }

  connect() {
    if (this.closed) return;
    clearTimeout(this.reconnectTimer);
    clearInterval(this.hb);
    const old = this.ws;
    this.ws = null; // invalidate old callbacks before closing
    try { old?.close(); } catch {}
    let ws;
    try { ws = new WebSocket(wsUrl(`/api/session/${this.sessionId}/ws`)); }
    catch { this.scheduleReconnect(null); return; }
    this.ws = ws;
    this.lastMsgAt = Date.now();
    const active = () => !this.closed && this.ws === ws;
    // CONNECTING sockets can go silent too.
    this.hb = setInterval(() => {
      if (!active() || this.paused) return;
      if (Date.now() - this.lastMsgAt > DEAD_MS) return this.connect();
      if (ws.readyState === WebSocket.OPEN) this.send("hb");
    }, HEARTBEAT_MS);
    ws.onopen = () => {
      if (!active()) return;
      this.retry = 0;
      this.lastMsgAt = Date.now();
      for (const m of this.extraOnOpen.values()) {
        if (!active()) return;
        try { ws.send(JSON.stringify(m)); } catch { return this.connect(); }
      }
      if (!this.paused) this.send("hb");
    };
    ws.onmessage = (e) => {
      if (!active()) return;
      let msg;
      try { msg = JSON.parse(e.data); } catch { return; }
      if (msg.type !== "ack" && (msg.type !== "state" || !msg.state)) return;
      this.reconnectNotified = false;
      this.lastMsgAt = Date.now();
      if (msg.type === "state") {
        msg.state.clockOffset = msg.state.serverNow - Date.now();
        if (msg.state.status === "ended") this.stop();
        this.onState(msg.state);
      }
    };
    ws.onclose = (e) => {
      if (!active()) return; // before touching the replacement's timers
      clearInterval(this.hb);
      if (e.code === 4001) {
        this.stop();
        this.onEnded?.(); // fetch authority if its final push was lost
        return;
      }
      this.scheduleReconnect(ws);
    };
    ws.onerror = () => {
      if (!active()) return;
      try { ws.close(); } catch { this.scheduleReconnect(ws); }
    };
  }

  scheduleReconnect(ws) {
    if (this.closed || this.ws !== ws) return;
    clearTimeout(this.reconnectTimer);
    const delay = Math.min(3000, 500 * 2 ** this.retry++);
    this.reconnectTimer = setTimeout(() => {
      if (!this.closed && this.ws === ws) this.connect();
    }, delay);
    // Failed upgrades expose no HTTP status to JS. Fetch authority rather than
    // inventing an end or reconnecting forever from a stale initial snapshot.
    // Notify once until a current socket receives a valid ack/state, not onopen.
    if (!this.reconnectNotified) {
      this.reconnectNotified = true;
      this.onReconnect?.();
    }
  }

  send(type) {
    if (this.closed || this.ws?.readyState !== WebSocket.OPEN) return false;
    try { this.ws.send(JSON.stringify({ type })); return true; }
    catch { return false; }
  }

  sendSticky(type) {
    if (this.closed) return;
    this.extraOnOpen.set(type, { type });
    this.send(type);
  }

  forgetSticky(type) {
    this.extraOnOpen.delete(type);
  }

  setMediaReady(ready) {
    this.forgetSticky(ready ? "rtc_left" : "rtc_joined");
    this.sendSticky(ready ? "rtc_joined" : "rtc_left");
  }

  // Test-only caller in test-media.js. Keep socket-open pause semantics.
  pause(ms) {
    if (this.closed) return;
    this.paused = true;
    clearTimeout(this.pauseTimer);
    this.pauseTimer = setTimeout(() => {
      if (this.closed) return;
      this.paused = false;
      this.lastMsgAt = Date.now();
      this.send("hb");
    }, ms);
  }

  stop() {
    if (this.closed) return;
    this.closed = true;
    clearInterval(this.hb);
    clearTimeout(this.reconnectTimer);
    clearTimeout(this.pauseTimer);
    this.extraOnOpen.clear();
    document.removeEventListener("visibilitychange", this.onVisible);
    window.removeEventListener("online", this.onOnline);
    const ws = this.ws;
    this.ws = null;
    try { ws?.close(1000); } catch {}
  }
}
